import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ResourceRecord } from '@/settings/components/resources/ResourceCard';

/**
 * Canonical React Query key for a practice's resource registry.
 */
export function resourcesListQueryKey(
  practiceId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'resources-list', practiceId];
}

/** Wire shape of `GET /api/Resource` (ResourceListResponse, PROD-2008 / PR #942 + #944). */
export interface ResourceListResponse {
  resources: ResourceRecord[];
  /** Every resource at the practice, both statuses — NOT the page, NOT the filter. */
  totalCount: number;
  activeCount: number;
  archivedCount: number;
  /** Rows matching the request's search/status filter, before paging. */
  filteredCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ResourceRegistry {
  /** Every resource at the practice, both statuses, in server order. */
  resources: ResourceRecord[];
  totalCount: number;
  activeCount: number;
  archivedCount: number;
}

/** `MaxPageSize` in ResourceService.cs — asking for more is clamped server-side. */
const MAX_PAGE_SIZE = 200;

/**
 * The device registry for one practice — PROD-2010 (epic PROD-2007), backed by
 * PROD-2008 / PracticeManagement PR #942 + #944. Every row is a device; the
 * `type` query parameter still exists server-side but is pointless now and is
 * not sent.
 *
 *   GET /api/Resource?practiceId={id}&status=all&page={n}&pageSize=200
 *
 * Reads the WHOLE practice rather than one server page, then lets the page
 * component search / filter / paginate in memory. Two reasons, both load-bearing:
 *
 *  1. The name-uniqueness guard (F3, unique per practice,
 *     case-insensitive) has to see every row at the practice. Validating
 *     against a server page would miss a duplicate sitting on another page or
 *     behind a filter — exactly the Codex gate on #894 that `uniquenessSource`
 *     exists to close.
 *  2. `ResourceEditPage` resolves its record by id from this same cache: the
 *     API deliberately has no `GET /api/Resource/{id}` — list, create, rename,
 *     archive, restore is the whole surface.
 *
 * The cost is bounded: a registry is per practice, and the service materialises
 * the practice's rows on every request anyway (ResourceService.GetResources) to
 * compute the practice-wide counts. Dev holds 952 rows across ALL practices.
 * If one practice ever outgrows this, move search/filter/page to the server and
 * give the uniqueness guard its own endpoint — not before.
 */
export function useResourcesList(practiceId: string | null | undefined) {
  return useQuery<ResourceRegistry>({
    queryKey: resourcesListQueryKey(practiceId),
    queryFn: async ({ signal }) => {
      if (!practiceId) {
        throw new Error('useResourcesList called without practiceId');
      }
      const url = (page: number) =>
        `/api/Resource?practiceId=${encodeURIComponent(practiceId)}` +
        `&status=all&page=${page}&pageSize=${MAX_PAGE_SIZE}`;

      const first = await get<ResourceListResponse>(url(1), { signal });
      const resources = [...(first.resources ?? [])];
      for (let page = 2; page <= (first.totalPages ?? 1); page += 1) {
        const next = await get<ResourceListResponse>(url(page), { signal });
        resources.push(...(next.resources ?? []));
      }
      return {
        resources,
        totalCount: first.totalCount ?? resources.length,
        activeCount: first.activeCount ?? 0,
        archivedCount: first.archivedCount ?? 0,
      };
    },
    enabled: !!practiceId,
    staleTime: 30_000,
  });
}
